import React, { useEffect, useRef, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import type { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { BVTConstants } from '../../../utils/BVTConstants';

export interface ScoreMeterProps {
  label: string;
  score: string;
  scoreRange: string;
  sx?: SxProps;
  loading?: boolean;
}

const ARC_COLORS = [
  '#F44336', // Red (Poor)
  '#FF9800', // Orange (Need Improvement)
  '#8BC34A', // Light Green (Strong)
  '#4CAF50', // Green (Excellent)
];
const ARC_LABELS = [
  BVTConstants.SCORE_RANGE_POOR,
  BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT,
  BVTConstants.SCORE_RANGE_STRONG,
  BVTConstants.SCORE_RANGE_EXCELLENT,
];
const ARC_LABEL_COLORS = ARC_COLORS;

// Map range to angle (center of each arc segment)
const rangeToAngle = {
  [BVTConstants.SCORE_RANGE_POOR]: 195, // center of first segment
  [BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT]: 240, // center of second segment
  [BVTConstants.SCORE_RANGE_STRONG]: 295, // center of third segment
  [BVTConstants.SCORE_RANGE_EXCELLENT]: 350, // center of fourth segment
};

const rangeToColor = {
  [BVTConstants.SCORE_RANGE_POOR]: '#F44336',
  [BVTConstants.SCORE_RANGE_NEED_IMPROVEMENT]: '#FF9800',
  [BVTConstants.SCORE_RANGE_STRONG]: '#8BC34A',
  [BVTConstants.SCORE_RANGE_EXCELLENT]: '#4CAF50',
};

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  // Convert angles to radians
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    'M', start.x, start.y,
    'A', r, r, 0, largeArcFlag, 0, end.x, end.y,
  ].join(' ');
}

function polarToCartesian(cx: number, cy: number, r: number, angle: number) {
  const rad = (angle - 90) * Math.PI / 180.0;
  return {
    x: cx + (r * Math.cos(rad)),
    y: cy + (r * Math.sin(rad)),
  };
}

export function ScoreMeter({
  label,
  score,
  scoreRange,
  sx,
  loading = false,
}: ScoreMeterProps): React.JSX.Element {
  const scoreValue = parseInt(score) || 0;
  const targetAngle = rangeToAngle[scoreRange] ?? 120;
  const scoreColor = rangeToColor[scoreRange] ?? '#F44336';

  // Arc settings
  const size = 260;
  const center = size / 2.5;
  const strokeWidth = 35; // Thicker arc band
  const radius = center - strokeWidth / 2; // Adjust so arc fits well
  const arcSegments = 4;
  const arcSweep = 180 / arcSegments;

  // Needle animation state
  const [needleAngle, setNeedleAngle] = useState(150);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    let start: number | null = null;
    const duration = 800;
    const initial = 150; 
    const delta = targetAngle - initial;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    function animate(ts: number) {
      if (start === null) start = ts;
      const elapsed = ts - start;
      const progress = Math.min(elapsed / duration, 1);
      setNeedleAngle(initial + delta * progress);
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setNeedleAngle(targetAngle);
      }
    }
    setNeedleAngle(initial);
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetAngle]);

  // Needle
  const needleBaseYOffset = 15; // move base up by 15px
  const needleBaseY = center - needleBaseYOffset;
  const needleLength = radius - 10;
  const needleRadians = (needleAngle * Math.PI) / 180;
  const needleX = center + needleLength * Math.cos(needleRadians);
  const needleY = needleBaseY + needleLength * Math.sin(needleRadians);

  return (
    <Card sx={{ ...sx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CardContent>
        <Stack spacing={0} alignItems="center">
          <Typography color="text.secondary" variant="overline" textAlign="center" sx={{ mb: 1, mt: -1 }}>
            {label}
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box position="relative" width={size} height={size / 2 + 10} mt={-1} ml={5}>
              <svg width={size} height={size / 2} style={{ display: 'block' }}>
                {/* Arc Segments with Tooltips */}
                {Array.from({ length: arcSegments }).map((_, i) => {
                  const start = -90 + i * arcSweep;
                  const end = start + arcSweep;
                  return (
                    <Tooltip key={i} title={ARC_LABELS[i]} arrow>
                      <path
                        d={describeArc(center, center, radius, start, end)}
                        fill="none"
                        stroke={ARC_COLORS[i]}
                        strokeWidth={strokeWidth}
                        strokeLinecap="butt"
                        style={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  );
                })}
                {/* Needle */}
                <line
                  x1={center}
                  y1={needleBaseY}
                  x2={needleX}
                  y2={needleY}
                  stroke="black"
                  strokeWidth={4}
                  strokeLinecap="round"
                />
                {/* Needle Pivot */}
                <circle
                  cx={center}
                  cy={needleBaseY}
                  r={8}
                  fill="black"
                />
              </svg>
              {/* Only Poor and Excellent Labels */}
              <Box
                sx={{
                  position: 'absolute',
                  top: size / 2 - 25,
                  left: -12,
                  width: '245px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 1.5,
                  zIndex: 5,
                }}
              >
                <Typography variant="caption" color="#F44336" fontWeight={600}>
                  {BVTConstants.SCORE_RANGE_POOR}
                </Typography>
                <Typography variant="caption" color="#4CAF50" fontWeight={600}>
                  {BVTConstants.SCORE_RANGE_EXCELLENT}
                </Typography>
              </Box>
              {/* Score and range below arc */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '44%',
                  top: size / 2 - 32,
                  width: 120,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  zIndex: 5,
                }}
              >
                <Typography variant="h5" fontWeight="bold" color={scoreColor}>
                  {scoreValue}%
                </Typography>
              </Box>
              {/* <Box
                sx={{
                  position: 'absolute',
                  left: '40%',
                  top: size / 2 - 10,
                  width: 120,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  zIndex: 5,
                }}
              >
                <Typography variant="caption" color={scoreColor}>
                  {scoreRange}
                </Typography>
              </Box> */}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
} 